import React, { useState, useEffect } from "react";
import { X, Mail, Lock, User as UserIcon, Loader2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function fallbackFromStatus(status, mode) {
  if (status === 401) return "Invalid email or password.";
  if (status === 409) return "An account with this email already exists.";
  if (status === 422) {
    return mode === "signup"
      ? "Password must be at least 8 characters. Please check your details."
      : "Please check your email and password.";
  }
  if (status === 429) return "Too many failed attempts. Try again in 15 minutes.";
  return "Something went wrong. Please try again.";
}

async function readResponseBody(res) {
  // The Emergent badge script may consume the original body for non-2xx responses.
  // Clone first, then try text → JSON. Fall back to an empty object.
  try {
    const txt = await res.clone().text();
    if (!txt) return {};
    try { return JSON.parse(txt); } catch { return { detail: txt }; }
  } catch {
    return {};
  }
}

// Password strength: 4 criteria → 0..4 score
// score < 2 = Weak (block submit), 2-3 = Fair, 4 = Strong
function evaluatePasswordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "bg-slate-200", textColor: "text-slate-400", widthPct: 0 };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const tiers = [
    { label: "Too short", color: "bg-[#EF5350]", textColor: "text-[#C62828]", widthPct: 15 },
    { label: "Weak",      color: "bg-[#EF5350]", textColor: "text-[#C62828]", widthPct: 30 },
    { label: "Fair",      color: "bg-[#FFB74D]", textColor: "text-[#E65100]", widthPct: 60 },
    { label: "Good",      color: "bg-[#FFB74D]", textColor: "text-[#E65100]", widthPct: 80 },
    { label: "Strong",    color: "bg-[#66BB6A]", textColor: "text-[#2E7D32]", widthPct: 100 },
  ];
  return { score, ...tiers[score] };
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthModal({ open, onClose, onAuthenticated, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (open) { setMode(initialMode); setInfo(""); setError(""); }
  }, [open, initialMode]);

  if (!open) return null;

  const reset = () => {
    setEmail(""); setPassword(""); setName(""); setError(""); setInfo("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");

    // Block weak passwords on signup before hitting the network.
    if (mode === "signup") {
      const s = evaluatePasswordStrength(password);
      if (s.score < 2) {
        setError("Your password is too weak. Use at least 8 characters with a mix of upper-case, numbers, and a symbol.");
        setLoading(false);
        return;
      }
    }

    let endpoint, body;
    if (mode === "signup") {
      endpoint = "/auth/register";
      body = { email, password, name };
    } else if (mode === "forgot") {
      endpoint = "/auth/forgot-password";
      body = { email };
    } else {
      endpoint = "/auth/login";
      body = { email, password };
    }

    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await readResponseBody(res);
      if (!res.ok) {
        const msg = formatApiErrorDetail(data.detail) || fallbackFromStatus(res.status, mode);
        setError(msg && msg !== "Something went wrong. Please try again." ? msg : fallbackFromStatus(res.status, mode));
        return;
      }
      if (mode === "forgot") {
        setInfo(data.message || "If that email is registered, a reset link has been sent.");
        return;
      }
      onAuthenticated(data);
      onClose();
      reset();
    } catch (err) {
      const msg = err?.message || String(err) || "unknown";
      // TypeError: Failed to fetch = CORS / network / DNS; everything else is unexpected
      if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        setError("Can't reach the server. Please check your connection or try again in a moment.");
      } else {
        setError(`Unexpected error: ${msg}`);
      }
      // eslint-disable-next-line no-console
      console.error("[auth] fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="auth-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in-up font-['Figtree']">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-xl font-extrabold text-slate-800 font-['Nunito']">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="auth-modal-close">
            <X size={18} />
          </button>
        </div>

        {/* Tabs — hidden in 'forgot' mode */}
        {mode !== "forgot" && (
          <div className="flex gap-1 px-6 pt-4">
            <button
              data-testid="auth-tab-signin"
              onClick={() => switchMode("signin")}
              className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all ${mode === "signin" ? "bg-[#FF8A65] text-white" : "text-slate-500 hover:bg-slate-100"}`}
            >Sign in</button>
            <button
              data-testid="auth-tab-signup"
              onClick={() => switchMode("signup")}
              className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all ${mode === "signup" ? "bg-[#FF8A65] text-white" : "text-slate-500 hover:bg-slate-100"}`}
            >Create account</button>
          </div>
        )}

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl">
              {error}
            </div>
          )}

          {info && (
            <div data-testid="auth-info" className="text-sm text-[#1565C0] bg-[#E3F2FD] border border-[#BBDEFB] px-4 py-2.5 rounded-xl">
              {info}
            </div>
          )}

          {mode === "forgot" && (
            <p className="text-sm text-slate-500 leading-relaxed">
              Enter your email and we'll send you a link to set a new password.
            </p>
          )}

          {mode === "signup" && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Your name</label>
              <div className="relative">
                <UserIcon size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Alex Park"
                  required
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
              />
            </div>
          </div>

          {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600">Password</label>
                {mode === "signin" && (
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => switchMode("forgot")}
                    className="text-xs font-semibold text-[#FF8A65] hover:text-[#FF7043]"
                  >Forgot password?</button>
                )}
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "At least 8 characters" : "Your password"}
                  required
                  minLength={mode === "signup" ? 8 : undefined}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-[5.5rem] py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 focus:border-[#FF8A65]"
                />
                <button
                  type="button"
                  data-testid="auth-password-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#FF8A65] bg-[#FFF5E6] hover:bg-[#FFDDB3] transition-colors"
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={2.5} /> : <Eye size={16} strokeWidth={2.5} />}
                  <span>{showPassword ? "Hide" : "Show"}</span>
                </button>
              </div>
              {mode === "signup" && password.length > 0 && (() => {
                const s = evaluatePasswordStrength(password);
                return (
                  <div className="mt-2" data-testid="password-strength">
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${s.color}`}
                        style={{ width: `${s.widthPct}%` }}
                        data-testid="password-strength-bar"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${s.textColor}`} data-testid="password-strength-label">
                        {s.label}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        Use 8+ chars, an uppercase, a number, and a symbol.
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
          </button>

          {mode === "forgot" && (
            <button
              type="button"
              data-testid="auth-back-to-signin"
              onClick={() => switchMode("signin")}
              className="w-full text-xs font-semibold text-slate-500 hover:text-[#FF8A65] py-1"
            >
              ← Back to sign in
            </button>
          )}

          {/* Divider + Google — not shown in forgot mode */}
          {mode !== "forgot" && (
            <>
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-400">or</span></div>
              </div>

              <button
                type="button"
                data-testid="auth-google-btn"
                onClick={googleLogin}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <p className="text-xs text-slate-400 text-center pt-2">
                By continuing, you agree to our <a href="/terms" className="text-slate-500 hover:text-[#FF8A65] underline">Terms</a> and <a href="/privacy" className="text-slate-500 hover:text-[#FF8A65] underline">Privacy Policy</a>.
              </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
